import Bluebird from 'bluebird'
import { createContext, useCallback, useContext, useState } from 'react'
import { v4 as uuid4 } from 'uuid'
import { useTypedTranslation } from './useTypedTranslation'

export enum DossSignalType {
  ACTIVITY_FEED_COLLAPSED = 'ACTIVITY_FEED_COLLAPSED',
  ACTIVITY_FEED_EXPANDED = 'ACTIVITY_FEED_EXPANDED',
  CHART_NAME_SAVED = 'CHART_NAME_SAVED',
  CLOSE_CREATE_PANEL = 'CLOSE_CREATE_PANEL',
  CLOSE_NOTIFICATIONS_PANEL = 'CLOSE_NOTIFICATIONS_PANEL',
  CLOSE_RIGHT_PANEL = 'CLOSE_RIGHT_PANEL',
  COLLAPSE_ACTIVITY_FEED_CLICKED = 'COLLAPSE_ACTIVITY_FEED_CLICKED',
  COLLAPSE_LEFT_NAV_CLICKED = 'COLLAPSE_LEFT_NAV_CLICKED',
  CREATE_TABLE_TAB = 'CREATE_TABLE_TAB',
  DELETE_FORM = 'DELETE_FORM',
  DUPLICATE_FORM = 'DUPLICATE_FORM',
  END_CATEGORY_DRAG = 'END_CATEGORY_DRAG',
  END_FIELD_DRAG = 'END_FIELD_DRAG',
  END_PAGE_DRAG = 'END_PAGE_DRAG',
  EXPAND_ACTIVITY_FEED_CLICKED = 'EXPAND_ACTIVITY_FEED_CLICKED',
  EXPAND_LEFT_NAV_CLICKED = 'EXPAND_LEFT_NAV_CLICKED',
  LEFT_NAV_COLLAPSED = 'LEFT_NAV_COLLAPSED',
  LEFT_NAV_EXPANDED = 'LEFT_NAV_EXPANDED',
  NEW_CHART_CREATED = 'NEW_CHART_CREATED',
  OPEN_CREATE_PANEL = 'OPEN_CREATE_PANEL',
  OPEN_RIGHT_PANEL = 'OPEN_RIGHT_PANEL',
  RECORD_FORM_DIRTY_STATE_CHANGE = 'RECORD_FORM_DIRTY_STATE_CHANGE',
  RECORD_FORM_SAVE_AND_DUPLICATE_SAVE_COMPLETED = 'RECORD_FORM_SAVE_AND_DUPLICATE_SAVE_COMPLETED',
  RECORD_FORM_SAVE_AND_DUPLICATE_SUBMITTED = 'RECORD_FORM_SAVE_AND_DUPLICATE_SUBMITTED',
  REFETCH_TABLE_RECORDS = 'REFETCH_TABLE_RECORDS',
  SAVE_CHART_CLICKED = 'SAVE_CHART_CLICKED',
  SAVE_FORM = 'SAVE_FORM',
  START_CATEGORY_DRAG = 'START_CATEGORY_DRAG',
  START_FIELD_DRAG = 'START_FIELD_DRAG',
  START_PAGE_DRAG = 'START_PAGE_DRAG',
  THRESHOLDS_INPUT_ERROR = 'THRESHOLDS_INPUT_ERROR',
  TOGGLE_MOBILE_MENU = 'TOGGLE_MOBILE_MENU',
  UPDATE_FORM = 'UPDATE_FORM',
  WORKSPACE_NAME_SAVED = 'WORKSPACE_NAME_SAVED',
  DELETE_VIEW = 'DELETE_VIEW',
  REFETCH_VIEW_DATA = 'REFETCH_VIEW_DATA',
  EDIT_EMBED_SOURCE = 'EDIT_EMBED_SOURCE',
  REFRESH_WORKSPACE = 'REFRESH_WORKSPACE',
  DRAGGING_DASHBOARD_VIEW = 'DRAGGING_DASHBOARD_VIEW',
  RESET_PIVOT_TABLE_DRILLDOWN = 'RESET_PIVOT_TABLE_DRILLDOWN',
  INVALID_PIVOT_TABLE_NAME = 'INVALID_PIVOT_TABLE_NAME',
  VALID_PIVOT_TABLE_NAME = 'VALID_PIVOT_TABLE_NAME',
  FORMS_SEARCH_QUERY_CHANGED = 'FORMS_SEARCH_QUERY_CHANGED',
  OPEN_CREATE_FIELD_PANEL = 'OPEN_CREATE_FIELD_PANEL',
  COPY_TABLE_SCHEMA = 'COPY_TABLE_SCHEMA',
  TABLE_SETTINGS_ACTIONS_DISABLED_CHANGED = 'TABLE_SETTINGS_ACTIONS_DISABLED_CHANGED',
}

// We want to allow for any type to be passed into the callback function
// oxlint-disable-next-line @typescript-eslint/no-explicit-any
type CallbackArgs = any

interface DossSignalsContextValue {
  subscribe: (signal: DossSignalType, callback: (args?: CallbackArgs) => Promise<void>) => string
  unsubscribe: (id: string) => void
  dispatch: (signal: DossSignalType, callbackArgs?: CallbackArgs) => void
  loading: boolean
  enableSaveButton: boolean
  setEnableSaveButton: (enable: boolean) => void
  rightPanelDirty: boolean
  setRightPanelDirty: (dirty: boolean) => void
  handleClosePanel: () => Promise<boolean>
  showPivotTableDrilldown: boolean
  setShowPivotTableDrilldown: (show: boolean) => void
  pivotTableDrilldownChartName: string
  setPivotTableDrilldownChartName: (name: string) => void
}

export function DossSignalsProvider({ children }: { children: React.ReactNode }) {
  const { t } = useTypedTranslation()
  const [loading, setLoading] = useState(false)
  const [rightPanelDirty, setRightPanelDirty] = useState(false)
  const [enableSaveButton, setEnableSaveButton] = useState(false)
  const [showPivotTableDrilldown, setShowPivotTableDrilldown] = useState(false)
  const [subscriptions, setSubscriptions] = useState<
    Record<string, { signal: DossSignalType; callback: (args?: CallbackArgs) => Promise<void> }>
  >({})

  // Use to keep track of the chart name for pivot table drilldown display so it does not show
  // "Untitled" after the user has set a name for the pivot table but have not yet saved it
  const [pivotTableDrilldownChartName, setPivotTableDrilldownChartName] = useState(
    t('title_bar.untitled'),
  )

  const dispatch = useCallback(
    async (signal: DossSignalType, callbackArgs?: CallbackArgs) => {
      setLoading(true)

      const callbacks = Object.values(subscriptions)
        .filter((sub) => sub.signal === signal)
        .map((sub) => sub.callback)

      await Bluebird.mapSeries(callbacks, async (callback) => {
        await callback(callbackArgs)
      }).finally(() => {
        setLoading(false)
      })
    },
    [subscriptions],
  )

  const subscribe = useCallback(
    (signal: DossSignalType, callback: (args?: CallbackArgs) => Promise<void>) => {
      const id = uuid4()
      setSubscriptions((subs) => ({ ...subs, [id]: { signal, callback } }))
      return id
    },
    [],
  )

  const unsubscribe = useCallback((id: string) => {
    setSubscriptions((subs) => {
      const newSubs = { ...subs }
      delete newSubs[id]
      return newSubs
    })
  }, [])

  const handleClosePanel = useCallback(async () => {
    if (rightPanelDirty) {
      const shouldContinue = confirm(
        'You have unsaved changes. Are you sure you want to leave this page?',
      )
      if (!shouldContinue) {
        return false
      }
    }

    setRightPanelDirty(false)
    await dispatch(DossSignalType.CLOSE_RIGHT_PANEL)
    await dispatch(DossSignalType.CLOSE_CREATE_PANEL)
    return true
  }, [dispatch, rightPanelDirty])

  return (
    <DossSignalsContext.Provider
      value={{
        dispatch,
        subscribe,
        unsubscribe,
        loading,
        enableSaveButton,
        setEnableSaveButton,
        rightPanelDirty,
        setRightPanelDirty,
        handleClosePanel,
        showPivotTableDrilldown,
        setShowPivotTableDrilldown,
        pivotTableDrilldownChartName,
        setPivotTableDrilldownChartName,
      }}
    >
      {children}
    </DossSignalsContext.Provider>
  )
}

export const useDossSignals = () => {
  const context = useContext(DossSignalsContext)

  if (!context) {
    throw new Error('useDossSignals must be used within a DossSignalsProvider')
  }

  return context
}

const DossSignalsContext = createContext<DossSignalsContextValue | null>(null)
